import { Node, NodeType } from 'prosemirror-model';
import { PluginKey } from 'prosemirror-state';

import { EditorView, getEditorContainerClientRect } from '@lyearn/lygo-core';

import { PopperProps } from '../../../types';
import { blockType } from '../../../types/menus';
import {
  addList,
  AddMenuPluginKey,
  addTextBlock,
  getBlockMenuBaseLeftOffset,
  getCurrentSelectionNode,
  getElementPos,
  selectionType,
  setPluginMeta,
  setSelection,
} from '../../../utils';
import {
  BLOCK_MENU_BUTTON_HEIGHT,
  BLOCK_MENU_BUTTON_MARGIN,
  BLOCK_MENU_BUTTON_WIDTH,
  BLOCK_MENU_ITEM_HEIGHT,
  BLOCK_MENU_PADDING,
  BLOCK_MENU_WIDTH,
} from '../consts';
import { MenuType, PlacementType } from '../types';

export const blockMenuKey = new PluginKey('block-menu-plugin');

export const getTextOnlyNodeTypes = (view: EditorView) => {
  const addMenuBlocks = AddMenuPluginKey.getState(view.state)?.addMenuBlocks;
  return addMenuBlocks
    ? addMenuBlocks
        .filter((menuItem: blockType) => menuItem.isText)
        .map((menuItem: blockType) => menuItem.name)
    : [];
};

export const getSelectedMenuItemIndex = (currentlySelectedNode: Node, view: EditorView) => {
  const addMenuBlocks = AddMenuPluginKey.getState(view.state).addMenuBlocks;
  const textOnlyMenuItems = addMenuBlocks
    ? addMenuBlocks.filter((menuItem: blockType) => menuItem.isText)
    : [];
  let selectedMenuItemIndex = -1;
  let index = 0;
  for (const textOnlyMenuItem of textOnlyMenuItems) {
    if (
      textOnlyMenuItem.name === currentlySelectedNode.type.name &&
      (!textOnlyMenuItem.attrs ||
        !textOnlyMenuItem.attrs.level ||
        (currentlySelectedNode.attrs &&
          textOnlyMenuItem.attrs.level ===
            (currentlySelectedNode.attrs as { level: number }).level))
    ) {
      selectedMenuItemIndex = index;
      break;
    }
    index++;
  }
  return selectedMenuItemIndex;
};

export const convertLinkNodes = (view: EditorView) => [
  view.state.schema.nodes.embed,
  view.state.schema.nodes.webbookmark,
];

export const selectNode = (anchorEl: PopperProps['anchorEl'], view: EditorView) => {
  const elementPos = getElementPos(anchorEl, view);
  if (elementPos && elementPos.inside !== undefined) {
    setSelection(view, selectionType.NODE, elementPos.inside);
  }
};

export const onTurnIntoClick = (
  anchorEl: PopperProps['anchorEl'],
  block: blockType,
  view: EditorView,
) => {
  const elementPos = getElementPos(anchorEl, view);
  // config type in not available in EditorState of prosemirroe-model.
  const nodes = (view.state as any).config.schema.nodes;
  const newNode: NodeType = nodes[block.name];
  const currentSelectionNodeType = getCurrentSelectionNode(view, elementPos!.pos).type;
  if (elementPos && elementPos.inside !== undefined && elementPos.pos !== undefined) {
    setSelection(
      view,
      selectionType.TEXT,
      elementPos.pos +
        (!currentSelectionNodeType.isTextblock && !currentSelectionNodeType.isLeaf ? 1 : 0),
    );
  }
  if (newNode.isTextblock) {
    addTextBlock(view, block);
  } else if (!newNode.isTextblock && !newNode.isLeaf) {
    addList(view, block.name);
  }
  hideBlockMenu(view);
};

export const getBlockMenuDefaultProps = () => ({
  open: false,
  anchorEl: null,
  menuType: MenuType.GENERAL,
});

export const hideBlockMenu = (view: EditorView) => {
  if (blockMenuKey.getState(view.state)?.open) {
    const blockMenuProps = getBlockMenuDefaultProps();

    setPluginMeta(view, blockMenuKey, blockMenuProps);
  }
};

const getPlacementSide = (
  anchorElBoundingClientRect: DOMRect | null,
  textNodeCount: number,
  baseLeftOffset: number,
) => {
  const editorContainerBoundingClientRect = getEditorContainerClientRect();
  const BLOCK_MENU_MAX_HEIGHT = BLOCK_MENU_ITEM_HEIGHT * textNodeCount + BLOCK_MENU_PADDING;
  let placement, verticalSide, horizontalSide;

  // Find the vertical positing and popper placement.
  if (
    anchorElBoundingClientRect &&
    window.innerHeight - anchorElBoundingClientRect.top - BLOCK_MENU_BUTTON_HEIGHT <
      BLOCK_MENU_MAX_HEIGHT
  ) {
    placement = document.dir === 'rtl' ? 'right-end' : 'left-end';
    verticalSide = PlacementType.TOP;
  } else {
    verticalSide = PlacementType.BOTTOM;
    placement = document.dir === 'rtl' ? 'right-start' : 'left-start';
  }

  // Find the horizontal positing.
  if (
    editorContainerBoundingClientRect &&
    anchorElBoundingClientRect &&
    anchorElBoundingClientRect.left -
      editorContainerBoundingClientRect.left -
      baseLeftOffset -
      BLOCK_MENU_BUTTON_MARGIN -
      BLOCK_MENU_BUTTON_WIDTH <
      BLOCK_MENU_WIDTH
  ) {
    horizontalSide = PlacementType.RIGHT;
  } else {
    horizontalSide = PlacementType.LEFT;
  }

  return { placement, verticalSide, horizontalSide };
};

const getPlacementOffset = (
  anchorElBoundingClientRect: DOMRect | null,
  verticalSide: PlacementType,
  horizontalSide: PlacementType,
  baseLeftOffset: number,
) => {
  if (verticalSide === PlacementType.BOTTOM && horizontalSide === PlacementType.RIGHT) {
    return [0, -1 * BLOCK_MENU_WIDTH + baseLeftOffset];
  } else if (anchorElBoundingClientRect && verticalSide === PlacementType.TOP) {
    return [
      -anchorElBoundingClientRect.height + BLOCK_MENU_BUTTON_MARGIN + BLOCK_MENU_BUTTON_HEIGHT,
      (horizontalSide === PlacementType.RIGHT
        ? -1 * BLOCK_MENU_WIDTH
        : BLOCK_MENU_BUTTON_WIDTH + BLOCK_MENU_BUTTON_MARGIN) + baseLeftOffset,
    ];
  } else {
    return [0, BLOCK_MENU_BUTTON_WIDTH + BLOCK_MENU_BUTTON_MARGIN + baseLeftOffset];
  }
};

export const getBlockMenuPlacement = (anchorEl: PopperProps['anchorEl'], textNodeCount: number) => {
  const baseLeftOffset = getBlockMenuBaseLeftOffset(anchorEl);
  const anchorElBoundingClientRect = anchorEl ? (anchorEl as any).getBoundingClientRect() : null;
  let { placement, verticalSide, horizontalSide } = getPlacementSide(
    anchorElBoundingClientRect,
    textNodeCount,
    baseLeftOffset,
  );
  let offset = getPlacementOffset(
    anchorElBoundingClientRect,
    verticalSide,
    horizontalSide,
    baseLeftOffset,
  );
  return { offset, placement };
};
